import { Popup } from '../../../ui/popup/popup';
import React, { useCallback, useEffect, useState } from 'react';
import Preloader from '../../../ui/svg/common/preloader';
import { addToCartPopup } from '../data/data-service';
import { rootActions, useAppDispatch, useAppSelector } from '../data/store';
import TopPanel from './top-panel';
import AddedCartItem from './added-cart-item';
import YouMayAlsoLike from './you-may-also-like';
import { tiktokAddToCartPopup } from '../../../domain/marketing/tiktok-provider';
import { facebookAddToCartPopup } from '../../../domain/facebook/facebook-marketing-provider';
import {
    ConfirmationMsgVisibleReferer,
    IConfirmationMsgVisibleResult,
    isConfirmationMsgVisible
} from '../../shopping-cart/cart-bridger-pay/domain/free-plugin-provider';
import FreePluginBox from '../../shopping-cart/cart-bridger-pay/ui/free-plugin/free-plugin-box';
import { isEqual } from 'lodash';
import UpsellBox from '../../../domain/upsell/upsell-box/upsell-box';
import { UpsellReferrer } from '../../../domain/upsell/interfaces';
import FreePluginBoxMilestones
    from '../../shopping-cart/cart-bridger-pay/ui/free-plugin/free-plugin-box-milestones/free-plugin-box-milestones';

interface IAddToCartPopup {
    productId: number;
}

export const addToCartPopupGaAction = 'AddToCartPopup';

const AddToCartPopup = ({ productId }: IAddToCartPopup) => {

    const dispatch = useAppDispatch();
    const cartData = useAppSelector(store => store.root.cartData, isEqual);

    const [isOpened, setIsOpened] = useState(true);
    const [loading, setLoading] = useState(true);

    const [showAddMoreBox, setShowAddMoreBox] = useState(false);
    const [addMoreBox, setAddMoreBox] = useState<IConfirmationMsgVisibleResult|null>(null);

    const addProductHandler = useCallback(async (productId: number) => {
        setLoading(true);
        const response = await addToCartPopup(productId);
        setLoading(false);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        const relatedData = response.data.relatedData;
        const cartData = relatedData?.cartData ?? null;
        const addedCartItem = relatedData?.addedCartItem ?? null;
        const addedCartItemAdditionalData = relatedData?.addedCartItemAdditionalData ?? null;
        const facebookAddToCartGuid = cartData?.facebookAddToCartGuid ?? relatedData?.facebookAddToCartGuid ?? '';
       
        dispatch(
            rootActions.setCartData(cartData),
        );

        dispatch(
            rootActions.main({
                addedCartItem,
                addedCartItemAdditionalData,
            })
        );

        // Handle marketing --------------------

        if(addedCartItem) {
            tiktokAddToCartPopup(addedCartItem);

            facebookAddToCartPopup(
                facebookAddToCartGuid,
                addedCartItem.skuNumber,
                addedCartItem.skuPrice
            );
        }

    }, [dispatch]);

    useEffect(() => {
        (async () => {
            await addProductHandler(productId);
        })();
    }, [productId, addProductHandler]);

    /**
     * https://wavesaudio.atlassian.net/browse/DSWEB-5070
     * https://wavesaudio.atlassian.net/browse/MRKT-27499
     */
    useEffect(() => {
        if(loading || !cartData) return;

        // https://wavesaudio.atlassian.net/browse/DSWEB-5070
        // https://wavesaudio.atlassian.net/browse/MRKT-27499
        const confirmationMessageVisibility = isConfirmationMsgVisible(
            cartData,
            ConfirmationMsgVisibleReferer.AddToCartPopup
        );
        if(!confirmationMessageVisibility) return;

        const addMoreEnabled = confirmationMessageVisibility.addMoreEnabled;
        const progressBarEnabled = confirmationMessageVisibility.progressBarEnabled;
        const isEnabled = addMoreEnabled || progressBarEnabled;

        if(!isEnabled) return;

        setShowAddMoreBox(isEnabled);
        setAddMoreBox(confirmationMessageVisibility);
    }, [
        cartData,
        loading,
    ]);

    const close = () => {
        setIsOpened(false);
    };

    return (
        <>
            {
                isOpened &&
                <Popup
                    width="750px"
                    close={ close }
                    classes="add-to-cart-popup max-w-full max-h-full"
                    styles={{
                        paddingLeft: 0,
                        paddingRight: 0,
                    }}>

                    {
                        loading &&
                        <div className="w-full h-full flex items-center justify-center max-w-full max-h-full" style={{
                            width: '650px',
                            height: '600px',
                        }}>
                            <Preloader width="45px" height="25px" color="#4f4f4f" />
                        </div>
                    }

                    {
                        !loading && cartData &&
                        <>
                            <TopPanel />

                            <div className="py-5 px-4 md:px-6">
                                <AddedCartItem />

                                {
                                    showAddMoreBox && !!addMoreBox &&
                                    <FreePluginBox
                                        visibility={ addMoreBox as IConfirmationMsgVisibleResult }
                                        cartData={ cartData }
                                        classes="mt-4"
                                    />
                                }

                                <FreePluginBoxMilestones
                                    cartData={ cartData }
                                    classes="mt-4"
                                    referrer={ ConfirmationMsgVisibleReferer.AddToCartPopup }
                                    cartStep={ 1 }
                                />

                                <UpsellBox
                                    cartData={ cartData }
                                    classes="mt-4"
                                    referrer={ UpsellReferrer.AddToCartPopup }
                                />

                                <YouMayAlsoLike addProductHandler={ addProductHandler }/>
                            </div>
                        </>
                    }
                </Popup>
            }
        </>
    )
};

export default AddToCartPopup;